import {
  DeleteIcon,
  Divider,
  GKButton,
  GKDatePicker,
  GKModal,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  PlusIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { GET_COMPANY_LIST } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import { schema } from "utils/validationSchema";

interface Props {
  portfolioDetailData: any;
}

const DividendModal = (props: Props & GKModalProps) => {
  const { open, setOpen, portfolioDetailData } = props;
  const { id } = useParams<{ id: string }>();

  const [companyData, setCompanyData] = useState([]);

  const { isFetching } = useFetchQuery({
    key: ["GET_ALL_COMPANY_BY_ETF"],
    route: GET_COMPANY_LIST(["ETF"]),
    success: (data: any) => {
      setCompanyData(
        data?.data?.data.map((dataItem: any) => {
          return {
            ...dataItem,
            name: dataItem?.companyName,
            value: dataItem?.gridCode,
          };
        }),
      );
    },
  });

  const { mutate: createETFTransaction, isLoading } = useMutation({
    mutationFn: (data: any) => axiosInstance.post("/etf_transaction/", data),
    onSuccess: () => {
      refetchQuery(QueryKeys.PORTFOLIO_EFT_TRANSACTION_LIST);
      setOpen(false);
    },
  });

  const form = useForm({
    defaultValues: {
      portfolio: +id || "",
      transactions: [
        {
          date: "",
          isinCode: "",
          gridCode: "",
          quantity: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "transactions",
  });

  return (
    <GKModal
      modalTitle="Add ETF Interest"
      open={open}
      setOpen={setOpen}
      width={"65%"}
      footer={
        <Grid2
          display={"flex"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          gap={1}
        >
          <GKButton
            variant="outlined"
            size="medium"
            form="cash-form"
            onClick={() => {
              setOpen(false);
            }}
          >
            Close
          </GKButton>
          <GKButton
            color="dark"
            variant="outlined"
            size="medium"
            type="submit"
            form="dividend-form"
            loading={isLoading}
          >
            Save
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="dividend-form"
        onSubmit={form.handleSubmit((values) => createETFTransaction(values))}
      >
        <Grid2 container spacing={1}>
          <Grid2 size={12}>
            <Typography fontWeight={600} fontSize={18} color={"#202939"}>
              {portfolioDetailData?.name}
            </Typography>
            <Typography
              color={"var(--neutral-500)"}
              fontWeight={500}
              fontSize={12}
            >
              {portfolioDetailData?.portfolioId}
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>
          <Grid2 size={12}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Units</TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell
                          style={{
                            minWidth: "300px",
                          }}
                        >
                          <Controller
                            name={`transactions.${index}.gridCode`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please Select Company",
                              },
                            }}
                            render={({ field, formState: { errors } }) => (
                              <GKSearchSelect
                                {...field}
                                requiredField
                                disableClearable
                                loading={isFetching}
                                renderOption={(props, option) => (
                                  <li
                                    {...props}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <Typography
                                      color={"var(--white)"}
                                      fontSize={8}
                                      style={{
                                        background:
                                          option?.instType === "EQ"
                                            ? "var(--secondary-900)"
                                            : "var(--orange-700)",
                                        padding: "2px 4px",
                                        borderRadius: 4,
                                      }}
                                    >
                                      {option?.instType}
                                    </Typography>
                                    <Typography whiteSpace={"nowrap"}>
                                      {option?.name}
                                    </Typography>
                                  </li>
                                )}
                                options={companyData || []}
                                getOptionLabel={(option) => {
                                  return option?.name || option;
                                }}
                                onChange={(ev, val) => {
                                  field.onChange(val ? val?.value : null);
                                  form.setValue(
                                    `transactions.${index}.isinCode`,
                                    companyData?.find(
                                      (dataItem: any) =>
                                        dataItem?.value === val?.value,
                                    )?.isin,
                                  );
                                }}
                                value={
                                  companyData?.find(
                                    (data: any) => data?.value === field.value,
                                  )?.name
                                }
                                error={
                                  !!errors?.transactions?.[index]?.gridCode
                                }
                                helperText={
                                  errors?.transactions?.[index]?.gridCode
                                    ?.message
                                }
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`transactions.${index}.date`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please select date",
                              },
                              validate: async (value) => {
                                try {
                                  await schema("date").validate({
                                    date: dayjs(value).format("YYYY-MM-DD"),
                                  });
                                } catch (error: any) {
                                  return error.message;
                                }

                                return {};
                              },
                            }}
                            render={({ field, formState: { errors } }) => (
                              <GKDatePicker
                                {...field}
                                requiredField
                                value={dayjs(field.value)}
                                onChange={(value) => {
                                  field.onChange(
                                    dayjs(value).format("YYYY-MM-DD"),
                                  );
                                }}
                                disableFuture
                                slotProps={{
                                  textField: {
                                    error:
                                      !!errors?.transactions?.[index]?.date,
                                  },
                                }}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`transactions.${index}.quantity`}
                            control={form.control}
                            rules={{
                              required: {
                                value: true,
                                message: "Please Enter Units",
                              },
                            }}
                            render={({ field, formState: { errors } }) => (
                              <GKTextField
                                {...field}
                                type="number"
                                requiredField
                                error={!!errors.transactions?.[index]?.quantity}
                                onChange={(e: any) =>
                                  field.onChange(e.target.valueAsNumber)
                                }
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            className="outline"
                            color="error"
                            disabled={fields.length === 1}
                            onClick={() => remove(index)}
                            size="small"
                          >
                            <DeleteIcon width="18" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={4}>
                      <GKButton
                        variant="outlined"
                        size="medium"
                        onClick={() => {
                          append({
                            date: "",
                            isinCode: "",
                            gridCode: "",
                            quantity: "",
                          });
                        }}
                        startIcon={<PlusIcon width={18} />}
                      >
                        Add ETF Interest
                      </GKButton>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default DividendModal;
